import {
  CLIENTS_SECTION_URL,
  ENROLLMENT_PLANNER_SECTION_URL,
  ORGANIZATIONS_SECTION_URL,
  REPORTING_SECTION_URL
} from 'constants/utils';

import { Avatar, SelectChangeEvent } from '@mui/material';
import clsx from 'clsx';
import { Heading, Logo, NavBar, Stack, Text } from 'components';
import { HelpButton, ProfilePopper } from 'modules';
import { FC, useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getUserInfo, getUserInfoImage } from 'store/profile/profileActions';
import { useIsOnlyVisibleToUserWithPermissions } from 'utils/hooks/useIsOnlyVisibleTo';
import { PERMISSION } from 'utils/protectedRoutes/userRolesAndPermissionTypes';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Link, useLocation } from 'react-router-dom';
import { ActionDropdown } from 'components/actionDropdown';
import { useResponsive } from 'utils/hooks/useMediaQuery';
import { Action, updateFilters } from 'store/clients/clientsSlice';

import style from './header.module.scss';
import { getLsUserRoleObject } from '../networkTools/localStorageTokens';

export const showPinnedHeader = (location: any) => {
  let showPinned = false;
  const { pathname } = location;
  showPinned = pathname.includes('ourWork');
  return showPinned;
};

interface Props {}

const actionData = [
  {
    id: 'RUN_THE_LIST',
    name: 'Run The List',
    info: 'These enrolled clients have an upcoming graduation date, haven’t had a manager review or been reached in their recommended interval, need their MPI, or don’t have any Roadmaps completed',
    tooltipPlacement: 'left',
    tooltipDelay: 1
  },
  {
    id: 'ENGAGE',
    name: 'Engage',
    info: 'These enrolled clients haven’t been reached in their recommended interval',
    tooltipPlacement: 'left',
    tooltipDelay: 1
  },
  {
    id: 'MPI',
    name: 'MPI',
    info: 'These enrolled clients need their MPI (MPI note, screener, and snapshot)',
    tooltipPlacement: 'left',
    tooltipDelay: 1
  },
  {
    id: 'OUTREACH',
    name: 'Outreach',
    info: 'These clients need outreach because they are in pre-outreach or pending status, or are re-eligible after not being reached, declining, or closing out',
    tooltipPlacement: 'left',
    tooltipDelay: 1
  },
  {
    id: 'SUPPORT',
    name: 'Support',
    info: 'These enrolled clients are starred or do not have any Roadmaps completed',
    tooltipPlacement: 'left',
    tooltipDelay: 1
  }
];

export const BoltIcon = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    xmlns="http://www.w3.org/2000/svg"
    style={{ marginBottom: '2px', zIndex: 20 }}
  >
    <path
      d="M10.6655 21C10.3155 21 10.0455 20.69 10.0955 20.34L10.9955 14H7.49545C6.61545 14 7.16545 13.25 7.18545 13.22C8.44545 10.99 10.3355 7.69 12.8355 3.29C12.9355 3.11 13.1355 3 13.3355 3C13.6855 3 13.9555 3.31 13.9055 3.66L13.0055 10H16.5155C16.9155 10 17.1355 10.19 16.9155 10.66C13.6255 16.4 11.7155 19.75 11.1655 20.71C11.0655 20.89 10.8755 21 10.6655 21Z"
      fill="#60768F"
    />
  </svg>
);

export const STATUSES_FOR_OUTREACH = [
  'PRE_OUTREACH',
  'PENDING',
  'NOT_REACHED',
  'DECLINED',
  'WITHDRAWN',
  'LOST_TO_FOLLOW_UP',
  'INELIGIBLE',
  'GRADUATED'
];

const Header: FC<Props> = () => {
  const { isTablet, isMobile } = useResponsive();
  const { pathname } = useLocation();
  const { aiScribe } = useFlags();
  const anchorRef = useRef(null);
  const dispatch = useAppDispatch();
  const profile = useAppSelector((state) => state.profile.info);
  const { filters } = useAppSelector((state) => state.clients);
  const profileImage = useAppSelector((state) => state.profile.profileImage);
  const userRoleData = getLsUserRoleObject();
  const isHelpButtonView = useIsOnlyVisibleToUserWithPermissions([PERMISSION.VIEW_HELP_BUTTON]);

  const isClientsPage = pathname === CLIENTS_SECTION_URL;
  const isNewClientPage = pathname === '/clients/new/profile';

  const [open, setOpen] = useState(false);

  const handleClick = () => {
    setOpen(!open);
  };

  const navItems = [
    {
      showItem: 'true',
      path: ORGANIZATIONS_SECTION_URL,
      name: 'Organizations',
      permission: [PERMISSION.ORG_READ],
      icon: 'domain'
    },
    {
      showItem: aiScribe && isTablet ? 'false' : 'true',
      path: ENROLLMENT_PLANNER_SECTION_URL,
      name: 'Enrollment',
      permission: [PERMISSION.ENROLL_READ],
      icon: 'groups_2'
    },
    {
      showItem: 'true',
      path: CLIENTS_SECTION_URL,
      name: 'Clients',
      permission: [PERMISSION.CLIENTS_READ],
      icon: 'diversity_4'
    },
    {
      showItem: aiScribe && isTablet ? 'false' : 'true',
      path: REPORTING_SECTION_URL,
      name: 'Reporting',
      permission: [PERMISSION.CLIENTS_READ],
      icon: 'bar_chart'
    }
  ];

  useEffect(() => {
    dispatch(getUserInfo(userRoleData?.organizationId));
  }, [userRoleData?.organizationId]);

  useEffect(() => {
    if (profile?.profileImageKey) {
      dispatch(getUserInfoImage(profile.profileImageKey));
    }
  }, [profile?.profileImageKey]);

  const onClose = () => {
    setOpen(false);
  };

  const onActionChange = (e: SelectChangeEvent<string | string[]>) => {
    const clientListContainer = document.getElementById('mobile-clients-list-scroll-container');
    if (clientListContainer) {
      clientListContainer.scrollTo({ top: 0, behavior: 'smooth' });
    }
    if (e.target.value === 'OUTREACH') {
      const payload = {
        action: e.target.value as Action,
        statuses: STATUSES_FOR_OUTREACH
      };
      dispatch(updateFilters(payload));
    } else if (e.target.value === '') {
      dispatch(
        updateFilters({
          action: null
        })
      );
    } else {
      const payload = {
        action: e.target.value as Action,
        statuses: ['ENROLLED']
      };
      dispatch(updateFilters(payload));
    }
  };

  if ((isClientsPage || isNewClientPage) && aiScribe && isTablet) {
    return (
      <Stack
        display="flex"
        flexDirection="column"
        gap="16px"
        bgcolor={isNewClientPage ? '#F8FAFC' : 'white'}
        px="20px"
        pt="16px"
        pb={isNewClientPage ? '16px' : '4px'}
      >
        <Stack
          display="flex"
          flexDirection="row"
          justifyContent="space-between"
          alignItems="center"
          bgcolor={isNewClientPage ? '#F8FAFC' : 'white'}
        >
          <Stack display="flex" flexDirection="row" alignItems="center" gap="16px">
            {isNewClientPage && (
              <Link to={CLIENTS_SECTION_URL} style={{ display: 'flex' }}>
                <span className="material-icons-outlined">chevron_left</span>
              </Link>
            )}
            <Heading level={4} as="h4" className={style.title}>
              {isClientsPage && 'Clients'}
              {isNewClientPage && 'New Client'}
            </Heading>
          </Stack>
          <Stack display="flex" flexDirection="row" alignItems="center">
            {isClientsPage && (
              <Stack width="144px" mr="16px">
                <ActionDropdown
                  size="normal"
                  label="Actions"
                  value={filters.action ?? ''}
                  data={actionData}
                  onChange={onActionChange}
                  dropdownFullWidth={isMobile}
                  rounded
                  className={clsx(style.filterDropdown, filters.action && style.selected)}
                  leftIcon={<BoltIcon />}
                  dropdownTooltipText="See who you need to outreach, engage, support, finish an MPI, or discuss with your manager"
                />
              </Stack>
            )}
            <div
              ref={anchorRef}
              onClick={handleClick}
              className={clsx(style.info, style.wrap)}
              data-test-id="profile-modal-btn"
            >
              <div className={style.title}>
                <Avatar
                  className={style.avatar}
                  style={{ width: '36px', height: '36px' }}
                  src={profileImage}
                >
                  <span className="material-icons-outlined">person</span>
                </Avatar>
              </div>
            </div>
            <ProfilePopper open={open} anchorEl={anchorRef.current} onClose={onClose} />
          </Stack>
        </Stack>
        {isNewClientPage && (
          <div>
            <Text size="normal" color="light">
              Pre-outreach
            </Text>
          </div>
        )}
      </Stack>
    );
  }

  return (
    <div className={clsx(style.headerContainer)}>
      <div className={style.header}>
        <div className={style.logo}>
          <Logo type="small" />
        </div>
        <NavBar navItems={navItems}></NavBar>

        <div className={style.wrap}>
          {isHelpButtonView && <HelpButton />}
          <>
            <div
              ref={anchorRef}
              onClick={handleClick}
              className={clsx(style.info, style.wrap)}
              data-test-id="profile-modal-btn"
            >
              <div className={style.title}>
                <Avatar className={style.avatar} src={profileImage}>
                  <span className="material-icons-outlined">person</span>
                </Avatar>
              </div>
              <span className={clsx('material-icons', style.icon, open && style.activeIcon)}>
                expand_more
              </span>
            </div>
            <ProfilePopper open={open} anchorEl={anchorRef.current} onClose={onClose} />
          </>
        </div>
      </div>
    </div>
  );
};

export default Header;
